import { useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Download, Pencil, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { bulkDeleteTaxRecords, deleteTaxRecord, listTaxRecords } from "@/features/transcripts/api";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { CsedPopover } from "@/features/transcripts/components/csed-popover";
import { TaxPeriodFormDialog } from "@/features/transcripts/components/tax-period-form-dialog";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import { exportToCsv } from "@/features/transcripts/lib/export-csv";
import type { FiledStatus, TaxRecord } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { ApiError } from "@/lib/api";
import { cn, formatCents } from "@/lib/utils";

const filedStatusClassName: Record<FiledStatus, string> = {
  Filed: "border-success/20 bg-success/10 text-success",
  "Not Filed": "border-warning/20 bg-warning/10 text-warning",
  SFR: "border-destructive/20 bg-destructive/10 text-destructive",
};

type SortKey = "taxYear" | "form" | "totalOwedCents";

function formLabel(record: TaxRecord): string {
  return record.quarter ? `${record.form} Q${record.quarter}` : record.form;
}

interface DeleteState {
  open: boolean;
  ids: string[];
  label: string;
}

export default function ClientTaxOwedPage() {
  const { client, forWho, dataVersion, refreshData } = useTranscriptsContext();
  const navigate = useNavigate();

  const [hideZero, setHideZero] = useState(false);
  const [hideUnfiled, setHideUnfiled] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("taxYear");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<TaxRecord | null>(null);
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false, ids: [], label: "" });

  const state = useResource<TaxRecord[]>(
    () => listTaxRecords(client.id, { forWho, hideZero, hideUnfiled }),
    [client.id, forWho, hideZero, hideUnfiled, dataVersion],
  );

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  }

  function sortIcon(key: SortKey) {
    if (key !== sortKey) return <ArrowUpDown className="size-3.5" />;
    return sortDirection === "asc" ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />;
  }

  function refreshAll() {
    setSelected(new Set());
    state.reload();
    refreshData();
  }

  async function confirmDelete() {
    const { ids } = deleteState;
    try {
      if (ids.length === 1 && ids[0]) {
        await deleteTaxRecord(client.id, ids[0]);
      } else {
        await bulkDeleteTaxRecords(client.id, ids);
      }
      toast.success(ids.length === 1 ? "Tax period deleted" : `${ids.length} tax periods deleted`);
      refreshAll();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Delete failed.");
    } finally {
      setDeleteState({ open: false, ids: [], label: "" });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Tax Owed</h1>
          <p className="text-muted-foreground max-w-2xl">
            Federal tax detail across every filed period for this client.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus /> Add Tax Period
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="hide-zero"
            checked={hideZero}
            aria-label="Hide $0 due"
            onCheckedChange={(v) => setHideZero(v === true)}
          />
          <Label htmlFor="hide-zero" className="text-muted-foreground font-normal">
            Hide $0 due
          </Label>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="hide-unfiled"
            checked={hideUnfiled}
            aria-label="Hide unfiled"
            onCheckedChange={(v) => setHideUnfiled(v === true)}
          />
          <Label htmlFor="hide-unfiled" className="text-muted-foreground font-normal">
            Hide unfiled
          </Label>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                setDeleteState({ open: true, ids: [...selected], label: `${selected.size} tax periods` })
              }
            >
              <Trash2 /> Delete ({selected.size})
            </Button>
          )}
          <ExportButton state={state} clientId={client.id} />
        </div>
      </div>

      <AsyncContent state={state}>
        {(records) => {
          const sorted = [...records].sort((a, b) => {
            const aVal = a[sortKey];
            const bVal = b[sortKey];
            const cmp =
              typeof aVal === "string"
                ? aVal.localeCompare(bVal as string)
                : (aVal as number) - (bVal as number);
            return sortDirection === "asc" ? cmp : -cmp;
          });
          const summary = summarize(sorted);
          const allSelected = sorted.length > 0 && sorted.every((r) => selected.has(r.id));

          function toggleAll(checked: boolean) {
            setSelected(checked ? new Set(sorted.map((r) => r.id)) : new Set());
          }
          function toggleRow(id: string, checked: boolean) {
            setSelected((prev) => {
              const next = new Set(prev);
              if (checked) next.add(id);
              else next.delete(id);
              return next;
            });
          }

          return (
            <Card className="gap-0 overflow-hidden p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        aria-label="Select all"
                        onCheckedChange={(v) => toggleAll(v === true)}
                      />
                    </TableHead>
                    <TableHead>
                      <button type="button" onClick={() => toggleSort("taxYear")} className="flex items-center gap-1">
                        Tax Year {sortIcon("taxYear")}
                      </button>
                    </TableHead>
                    <TableHead>
                      <button type="button" onClick={() => toggleSort("form")} className="flex items-center gap-1">
                        Form {sortIcon("form")}
                      </button>
                    </TableHead>
                    <TableHead>Filed</TableHead>
                    <TableHead>Filing</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Interest</TableHead>
                    <TableHead className="text-right">Penalty</TableHead>
                    <TableHead className="text-right">
                      <button
                        type="button"
                        onClick={() => toggleSort("totalOwedCents")}
                        className="ml-auto flex items-center gap-1"
                      >
                        Total Owed {sortIcon("totalOwedCents")}
                      </button>
                    </TableHead>
                    <TableHead>Est. CSED</TableHead>
                    <TableHead>Lien</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((record) => (
                    <TableRow
                      key={record.id}
                      role="button"
                      onClick={() => navigate(record.id)}
                      className="cursor-pointer"
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(record.id)}
                          aria-label={`Select ${formLabel(record)} ${record.taxYear}`}
                          onCheckedChange={(v) => toggleRow(record.id, v === true)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{record.taxYear}</TableCell>
                      <TableCell>{formLabel(record)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("text-[11px] font-bold uppercase", filedStatusClassName[record.filedStatus])}>
                          {record.filedStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{record.filingStatus ?? "—"}</TableCell>
                      <TableCell className="text-right">{formatCents(record.balanceCents)}</TableCell>
                      <TableCell className="text-right">{formatCents(record.accruedInterestCents)}</TableCell>
                      <TableCell className="text-right">{formatCents(record.accruedPenaltyCents)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatCents(record.totalOwedCents)}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {record.estimatedCsed ?? "—"}
                        {record.multiCsed && <span title="Multiple CSED dates"> *</span>}
                      </TableCell>
                      <TableCell>
                        {record.hasLien && <ShieldAlert className="text-destructive size-4" aria-label="Lien on file" />}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <CsedPopover clientId={client.id} recordId={record.id} />
                          <Button variant="ghost" size="icon-sm" aria-label="Edit" onClick={() => setEditRecord(record)}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Delete"
                            onClick={() =>
                              setDeleteState({
                                open: true,
                                ids: [record.id],
                                label: `${formLabel(record)} — ${record.taxYear}`,
                              })
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sorted.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} className="text-muted-foreground text-center">
                        No tax records for this selection.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5}>Total</TableCell>
                    <TableCell className="text-right">{formatCents(summary.balanceCents)}</TableCell>
                    <TableCell className="text-right">{formatCents(summary.accruedInterestCents)}</TableCell>
                    <TableCell className="text-right">{formatCents(summary.accruedPenaltyCents)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCents(summary.totalOwedCents)}</TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableFooter>
              </Table>
            </Card>
          );
        }}
      </AsyncContent>

      <TaxPeriodFormDialog
        clientId={client.id}
        hasSpouse={!!client.spouse}
        record={null}
        forWho={forWho}
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={refreshAll}
      />
      <TaxPeriodFormDialog
        clientId={client.id}
        hasSpouse={!!client.spouse}
        record={editRecord}
        forWho={forWho}
        open={editRecord !== null}
        onOpenChange={(open) => !open && setEditRecord(null)}
        onSaved={refreshAll}
      />

      <AlertDialog
        open={deleteState.open}
        onOpenChange={(open) => !open && setDeleteState({ open: false, ids: [], label: "" })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax period?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <span className="font-medium">{deleteState.label}</span>. Deleted records are
              hidden from this client's tax history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function summarize(records: TaxRecord[]) {
  return records.reduce(
    (totals, record) => ({
      balanceCents: totals.balanceCents + record.balanceCents,
      accruedInterestCents: totals.accruedInterestCents + record.accruedInterestCents,
      accruedPenaltyCents: totals.accruedPenaltyCents + record.accruedPenaltyCents,
      totalOwedCents: totals.totalOwedCents + record.totalOwedCents,
    }),
    { balanceCents: 0, accruedInterestCents: 0, accruedPenaltyCents: 0, totalOwedCents: 0 },
  );
}

function ExportButton({ state, clientId }: { state: { data: TaxRecord[] | undefined }; clientId: string }) {
  const records = state.data ?? [];
  function handleExport() {
    exportToCsv(`${clientId}-tax-owed.csv`, records, [
      { header: "Tax Year", accessor: (r) => r.taxYear },
      { header: "Form", accessor: (r) => formLabel(r) },
      { header: "Filed", accessor: (r) => r.filedStatus },
      { header: "Balance", accessor: (r) => formatCents(r.balanceCents) },
      { header: "Accrued Interest", accessor: (r) => formatCents(r.accruedInterestCents) },
      { header: "Accrued Penalty", accessor: (r) => formatCents(r.accruedPenaltyCents) },
      { header: "Total Owed", accessor: (r) => formatCents(r.totalOwedCents) },
      { header: "Estimated CSED", accessor: (r) => r.estimatedCsed ?? "" },
      { header: "Lien", accessor: (r) => (r.hasLien ? "Yes" : "No") },
    ]);
  }
  return (
    <Button variant="outline" size="sm" onClick={handleExport} disabled={records.length === 0}>
      <Download /> Export CSV
    </Button>
  );
}
